import { formatNumber } from './format';

/**
* The "cost" model for external API usage (VD-725 follow-up): a request's
* real cost is processing time, not a single count, because one slow request
* can cost as much as 80 fast ones. Every number this model is built from
* (replica count, point size, capacity, thresholds) always comes from the
* backend's own `cost` object on a response - nothing here is a hardcoded
* assumption about the current deployment, on purpose, so the UI stays
* correct if those settings ever change.
*/

export const formatPoints = (value) => formatNumber(value);

export const formatLoadPercent = (value, digits = 1) => {
    let n = Number(value);

    if (value === null || value === undefined || isNaN(n)) {
        return '0%';
    }

    return `${n.toFixed(digits)}%`;
};

/**
* A log or slow-request row's own `costPoints` is authoritative. Only use
* this to backfill an older row that predates the field, and only when the
* caller actually has the backend's `cost.pointMs` on hand (the stats and
* insights responses carry it; the plain logs list does not, so there the
* right call is to show "-" instead of guessing at a constant).
*/
export const estimateCostPoints = (durationMs, pointMs) => {
    if (!pointMs || durationMs === null || durationMs === undefined || isNaN(Number(durationMs))) {
        return null;
    }

    return Math.max(1, Math.ceil(Number(durationMs) / Number(pointMs)));
};

/**
* Badge color for a load percentage, thresholds read from the response's own
* `cost` object (heavyPeakLoadPercent, overloadMinutePercent) rather than
* assumed - falls back to the documented defaults (25% / 10%) only when a
* caller has no `cost` object at hand yet (e.g. first render before the
* stats call resolves).
*/
export const loadBadgeClass = (loadPercent, cost) => {
    let heavyAt = cost && cost.heavyPeakLoadPercent ? Number(cost.heavyPeakLoadPercent) : 25;
    let overloadAt = cost && cost.overloadMinutePercent ? Number(cost.overloadMinutePercent) : 10;
    let n = Number(loadPercent) || 0;

    if (n >= heavyAt) {
        return 'api-key-badge api-key-badge-expired';
    }

    if (n >= overloadAt) {
        return 'api-key-badge api-key-badge-grace';
    }

    return 'api-key-badge api-key-badge-active';
};

export const loadBarColor = (loadPercent, cost) => {
    let cls = loadBadgeClass(loadPercent, cost);

    if (cls.indexOf('expired') !== -1) return '#d03b3b';
    if (cls.indexOf('grace') !== -1) return '#a85400';

    return '#1b7a44';
};

/**
* Plain-language lines for the "How is this calculated" explainer, built
* entirely from the backend's own `cost` settings.
*/
export const costModelLines = (cost, lang) => {
    if (!cost) {
        return [];
    }

    let capacityPerMinute = cost.capacityPointsPerMinute
        || (cost.replicas && cost.pointMs ? Math.round((cost.replicas * 60000) / cost.pointMs) : null);

    return [
        `${cost.replicas} ${'replicas'.translate(lang)} × 60 ${'s'.translate(lang)} = ${cost.replicas * 60} ${'s of processing per minute'.translate(lang)}${capacityPerMinute ? ` = ${formatNumber(capacityPerMinute)} ${'points of capacity'.translate(lang)}` : ''}.`,
        `${'1 point'.translate(lang)} = ${cost.pointMs} ${'ms of processing, every request costs at least 1 point'.translate(lang)}.`,
        `${'Load %'.translate(lang)} = ${'the share of that capacity a period\'s requests used up - it can pass 100%, which means replicas were overloaded (parallel processing).'.translate(lang)}`,
        `${'Heavy user'.translate(lang)}: ${'a client whose worst single minute in the last 7 days used'.translate(lang)} ≥ ${cost.heavyPeakLoadPercent}% ${'of capacity'.translate(lang)}.`,
        `${'Overloaded minute'.translate(lang)}: ${'a minute at'.translate(lang)} ≥ ${cost.overloadMinutePercent}% ${'of capacity'.translate(lang)}.`,
    ];
};
